import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import CoachCard from "@/components/CoachCard";
import AlertForm from "@/components/AlertForm";
import FranceMap from "@/components/FranceMap";
import { HOME } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";

const CATEGORIES = [
    { id: "all", label: "Tous" },
    { id: "physical", label: "Corps" },
    { id: "mental", label: "Esprit" },
];

const OBJECTIVES = [
    { id: "", label: "Tous objectifs" },
    { id: "wellness", label: "Bien-être" },
    { id: "competition", label: "Compétition" },
];

const SPECIALTIES = {
    physical: ["Musculation", "Running", "CrossFit", "Boxe", "Yoga", "Prep Physique"],
    mental: [
        "Gestion du stress",
        "Confiance en soi",
        "Anti-Burnout",
        "Motivation",
        "Sortir de la solitude",
    ],
};

export default function Home() {
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("all");
    const [objective, setObjective] = useState("");
    const [city, setCity] = useState("");
    const [remote, setRemote] = useState(false);
    const [price, setPrice] = useState([30, 150]);
    const [specialty, setSpecialty] = useState("");
    const [coaches, setCoaches] = useState([]);
    const [allCoaches, setAllCoaches] = useState([]);
    const [loading, setLoading] = useState(true);

    const marqueeTags = useMemo(
        () => [
            "Zéro followers.",
            "Zéro influence.",
            "Résultats uniquement.",
            "Avis vérifiés.",
            "Objectifs atteints.",
            "Fidélité mesurée.",
        ],
        []
    );

    // Load the full directory once so the France map can show counts even when filters are active
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/coaches");
                setAllCoaches(data);
            } catch (_) {}
        })();
    }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            const params = {
                q: q || undefined,
                category: category !== "all" ? category : undefined,
                objective: objective || undefined,
                city: city || undefined,
                remote: remote || undefined,
                min_price: price[0],
                max_price: price[1],
                specialty: specialty || undefined,
            };
            try {
                const { data } = await api.get("/coaches", { params });
                setCoaches(data);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q, category, objective, city, remote, price, specialty]);

    // Picking a category clears any orphaned specialty from the opposite set
    const pickCategory = (next) => {
        setCategory(next);
        if (next === "all") {
            setSpecialty("");
        } else if (specialty && !SPECIALTIES[next].includes(specialty)) {
            setSpecialty("");
        }
    };

    return (
        <div>
            {/* HERO */}
            <section className="border-b border-white/10 bg-grid">
                <div className="mx-auto flex max-w-[1400px] flex-col gap-10 px-6 py-16 lg:py-24">
                    <div className="flex items-start justify-between gap-6">
                        <div className="max-w-3xl">
                            <div className="mb-4 inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
                                <span className="h-1.5 w-1.5 bg-[#FF2E2E]" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                                    Coaching anti-influence
                                </span>
                            </div>
                            <h1 className="font-display text-5xl uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl">
                                EARNIT —{" "}
                                <span className="text-[#FF2E2E]">Des résultats,</span>
                                <br />
                                pas des <span className="text-[#FF2E2E]">followers</span>.
                            </h1>
                            <p className="mt-6 max-w-xl text-sm uppercase tracking-widest text-neutral-400">
                                La première plateforme de coaching basée à 100 % sur le mérite
                                et la progression réelle. Corps &amp; Esprit.
                            </p>
                        </div>
                        <div className="hidden lg:block">
                            <div className="border border-white/10 p-6 text-right">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    En ligne
                                </div>
                                <div className="font-display text-5xl text-white">
                                    {coaches.length}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    coachs vérifiés
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Big category toggle — PHYSIQUE vs MENTAL */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <button
                            data-testid="big-toggle-physical"
                            onClick={() => pickCategory(category === "physical" ? "all" : "physical")}
                            aria-pressed={category === "physical"}
                            className={`group flex items-center justify-between border-2 px-6 py-6 text-left transition-all ${
                                category === "physical"
                                    ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                    : "border-white/15 bg-[#121212] text-white hover:border-white/40"
                            }`}
                        >
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">
                                    Catégorie 01 / Corps
                                </div>
                                <div className="mt-2 flex items-center gap-3 font-display text-3xl uppercase leading-none tracking-tight sm:text-4xl">
                                    <span className="text-2xl" aria-hidden="true">💪</span>
                                    PHYSIQUE
                                </div>
                            </div>
                            <span
                                className={`h-6 w-6 border-2 ${
                                    category === "physical"
                                        ? "border-white bg-white"
                                        : "border-white/40"
                                }`}
                                aria-hidden="true"
                            />
                        </button>

                        <button
                            data-testid="big-toggle-mental"
                            onClick={() => pickCategory(category === "mental" ? "all" : "mental")}
                            aria-pressed={category === "mental"}
                            className={`group flex items-center justify-between border-2 px-6 py-6 text-left transition-all ${
                                category === "mental"
                                    ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                    : "border-white/15 bg-[#121212] text-white hover:border-white/40"
                            }`}
                        >
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">
                                    Catégorie 02 / Esprit
                                </div>
                                <div className="mt-2 flex items-center gap-3 font-display text-3xl uppercase leading-none tracking-tight sm:text-4xl">
                                    <span className="text-2xl" aria-hidden="true">🧠</span>
                                    MENTAL &amp; BIEN-ÊTRE
                                </div>
                            </div>
                            <span
                                className={`h-6 w-6 border-2 ${
                                    category === "mental"
                                        ? "border-white bg-white"
                                        : "border-white/40"
                                }`}
                                aria-hidden="true"
                            />
                        </button>
                    </div>

                    {/* Specialty chip row — reactive to selected category */}
                    {category !== "all" && (
                        <div
                            data-testid={`specialty-chips-${category}`}
                            className="flex flex-wrap items-center gap-2 border border-white/10 bg-[#0f0f0f] p-4"
                        >
                            <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                Spécialité
                            </span>
                            {SPECIALTIES[category].map((s) => {
                                const active = specialty === s;
                                return (
                                    <button
                                        key={s}
                                        data-testid={`chip-${s}`}
                                        onClick={() => setSpecialty(active ? "" : s)}
                                        className={`border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                                            active
                                                ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                                : "border-white/10 text-neutral-300 hover:border-white/40 hover:text-white"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                );
                            })}
                            {specialty && (
                                <button
                                    data-testid="specialty-clear"
                                    onClick={() => setSpecialty("")}
                                    className="ml-auto font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-white"
                                >
                                    Effacer ✕
                                </button>
                            )}
                        </div>
                    )}

                    {category === "mental" && (
                        <div
                            data-testid="mental-disclaimer"
                            className="flex items-start gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3"
                        >
                            <span className="mt-0.5 h-2 w-2 flex-shrink-0 bg-[#FF2E2E]" aria-hidden="true" />
                            <p className="text-[11px] uppercase leading-relaxed tracking-wider text-neutral-400">
                                <span className="font-mono text-white">Note :</span>{" "}
                                Les coachs bien-être EarnIt accompagnent le développement
                                personnel et ne remplacent pas un suivi médical ou
                                psychiatrique.
                            </p>
                        </div>
                    )}

                    {/* Search bar */}
                    <div className="flex items-center border border-white/10 bg-[#121212]">
                        <Search className="ml-4 h-5 w-5 text-neutral-500" />
                        <Input
                            data-testid={HOME.heroSearch}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher par nom, spécialité, discipline..."
                            className="h-14 flex-1 rounded-none border-0 bg-transparent text-base text-white placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                        <button
                            data-testid={HOME.heroSearchBtn}
                            className="h-14 bg-[#FF2E2E] px-6 font-display text-lg uppercase tracking-tight text-white hover:bg-[#D61F1F]"
                        >
                            Rechercher
                        </button>
                    </div>

                    {/* Filters row */}
                    <div className="grid grid-cols-1 gap-6 border border-white/10 bg-[#0f0f0f] p-6 lg:grid-cols-3">
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Objectif
                            </Label>
                            <div className="mt-2 flex border border-white/10">
                                {OBJECTIVES.map((o) => (
                                    <button
                                        key={o.id || "any"}
                                        data-testid={
                                            o.id === "wellness"
                                                ? HOME.objectiveWellness
                                                : o.id === "competition"
                                                ? HOME.objectiveCompetition
                                                : undefined
                                        }
                                        onClick={() => setObjective(o.id)}
                                        className={`flex-1 px-2 py-2 text-xs uppercase tracking-widest transition-colors ${
                                            objective === o.id
                                                ? "bg-[#FF2E2E] text-white"
                                                : "text-neutral-400 hover:text-white"
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Localisation
                            </Label>
                            <div className="mt-2 flex gap-2">
                                <Input
                                    data-testid={HOME.cityInput}
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Paris, Lyon, Marseille..."
                                    className="h-10 flex-1 rounded-none border border-white/10 bg-[#0a0a0a] text-sm text-white placeholder:text-neutral-600 focus-visible:ring-1 focus-visible:ring-[#FF2E2E] focus-visible:ring-offset-0"
                                />
                                <div className="flex items-center gap-2 border border-white/10 px-3">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                        À distance
                                    </span>
                                    <Switch
                                        data-testid={HOME.remoteSwitch}
                                        checked={remote}
                                        onCheckedChange={setRemote}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Prix € {price[0]} — € {price[1]} / h
                            </Label>
                            <div className="mt-5 px-1">
                                <Slider
                                    data-testid={HOME.priceSlider}
                                    value={price}
                                    onValueChange={setPrice}
                                    min={20}
                                    max={200}
                                    step={5}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Marquee strip */}
            <div className="overflow-hidden border-b border-white/10 bg-[#FF2E2E]">
                <div className="marquee-track flex whitespace-nowrap py-3 font-display text-xl uppercase tracking-tight text-black">
                    {[...marqueeTags, ...marqueeTags, ...marqueeTags].map((t, i) => (
                        <span key={i} className="mx-6 inline-flex items-center gap-6">
                            {t}
                            <span className="inline-block h-1.5 w-1.5 bg-black" />
                        </span>
                    ))}
                </div>
            </div>

            {/* France map */}
            <FranceMap
                coaches={allCoaches}
                activeCity={city}
                onPick={(c) => setCity(c)}
            />

            {/* Results */}
            <section className="mx-auto max-w-[1400px] px-6 py-12">
                <div className="mb-6 flex items-end justify-between">
                    <h2 className="font-display text-3xl uppercase tracking-tight text-white sm:text-4xl">
                        Classés au mérite
                    </h2>
                    <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">
                        {coaches.length} résultats · triés par objectifs atteints + note vérifiée
                    </span>
                </div>
                {loading ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="aspect-[3/5] animate-pulse border border-white/10 bg-[#121212]"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {coaches.map((c) => (
                            <CoachCard key={c.coach_id} coach={c} />
                        ))}
                    </div>
                )}
            </section>

            <AlertForm />
        </div>
    );
}
